import { memo, useMemo } from "react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

const CHART_COLORS = ["#ec474e", "#518dca", "#fdc736", "#08263e", "#4ade80", "#f97316", "#a855f7", "#14b8a6", "#e879f9", "#84cc16"];

function parseChartBlock(text) {
  const regex = /```chart:(pie|bar)\n([\s\S]*?)```/g;
  const parts = [];
  let lastIdx = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIdx) {
      parts.push({ type: "text", content: text.slice(lastIdx, match.index) });
    }
    try {
      const data = JSON.parse(match[2].trim());
      parts.push({ type: match[1], data });
    } catch {
      parts.push({ type: "text", content: match[0] });
    }
    lastIdx = match.index + match[0].length;
  }

  if (lastIdx < text.length) {
    parts.push({ type: "text", content: text.slice(lastIdx) });
  }

  return parts.length ? parts : [{ type: "text", content: text }];
}

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-white/95 border border-gray-200 rounded-lg px-3 py-2 shadow-lg text-xs">
      <p className="font-semibold text-gray-800">{d.name || d.payload?.name}</p>
      <p className="text-gray-600">{typeof d.value === "number" ? d.value.toLocaleString() : d.value}</p>
    </div>
  );
};

const InlinePieChart = memo(({ data, compact }) => {
  const size = compact ? 180 : 220;
  return (
    <div className="my-3 flex flex-col items-center" data-testid="inline-pie-chart">
      <ResponsiveContainer width="100%" height={size}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={size * 0.2} outerRadius={size * 0.35} dataKey="value" nameKey="name" paddingAngle={2}>
            {data.map((_, i) => (
              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip content={<ChartTooltip />} />
          <Legend wrapperStyle={{ fontSize: compact ? 10 : 12 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
});

const InlineBarChart = memo(({ data, compact }) => {
  const height = compact ? 160 : 200;
  return (
    <div className="my-3" data-testid="inline-bar-chart">
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <XAxis dataKey="name" tick={{ fontSize: compact ? 9 : 11, fill: "#fff" }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: compact ? 9 : 11, fill: "#fff" }} axisLine={false} tickLine={false} width={40} />
          <Tooltip content={<ChartTooltip />} />
          <Bar dataKey="value" radius={[4, 4, 0, 0]}>
            {data.map((_, i) => (
              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
});

const ChatContent = memo(({ content, MarkdownComponent, markdownProps, compact = false }) => {
  const parts = useMemo(() => parseChartBlock(content), [content]);

  return (
    <>
      {parts.map((part, i) => {
        if (part.type === "pie") return <InlinePieChart key={i} data={part.data} compact={compact} />;
        if (part.type === "bar") return <InlineBarChart key={i} data={part.data} compact={compact} />;
        return <MarkdownComponent key={i} {...markdownProps}>{part.content}</MarkdownComponent>;
      })}
    </>
  );
});

export { ChatContent, parseChartBlock };
